'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Download, RefreshCw, FileImageIcon, ArrowLeft, Settings2, ZoomIn, ZoomOut } from 'lucide-react';
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getVectorToolCopy } from "@/lib/vector-tool-copy";

interface VectorizationOptions {
  colors: number;
  scale: number;
  simplify: boolean;
  ltres: number;
  qtres: number;
  strokewidth: number;
}

export default function VectorizationEditor({ 
  file, 
  onBack 
}: { 
  file: File; 
  onBack: () => void 
}) {
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getVectorToolCopy(locale);
  const [svgContent, setSvgContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState<string>('');
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  
  // High Fidelity Defaults
  const [options, setOptions] = useState<VectorizationOptions>({
    colors: 64,
    scale: 1,
    simplify: false,
    ltres: 1,
    qtres: 1,
    strokewidth: 0
  });

  const [viewMode, setViewMode] = useState<'split' | 'original' | 'vector'>('vector');
  const [zoom, setZoom] = useState(1);
  const workerRef = useRef<Worker | null>(null);

  // Inline Worker Code to avoid 404s
  const createWorkerCallback = () => {
      // Get the current origin (e.g. http://localhost:3000)
      const origin = window.location.origin;
      
      const code = `
        self.importScripts('${origin}/scripts/imagetracer.js'); 
        
        self.onmessage = (e) => {
            const { imgData, config } = e.data;
            try {
                // ImageTracer is globally available after importScripts
                // Use 'self.ImageTracer' or access via properties
                const tracer = self.ImageTracer || self['ImageTracer'];
                let svgstr = tracer.imagedataToSVG(imgData, config);
                
                const width = imgData.width;
                const height = imgData.height;
                
                // Regex to enforce 100% width/height for responsiveness
                svgstr = svgstr.replace(/(^|\\s)width="\\d+(\\.\\d+)?"/, '$1width="100%"');
                svgstr = svgstr.replace(/(^|\\s)height="\\d+(\\.\\d+)?"/, '$1height="100%"');
                
                if (!svgstr.includes('viewBox')) {
                    svgstr = svgstr.replace('<svg', \`<svg viewBox="0 0 \${width} \${height}"\`);
                }
                
                self.postMessage({ svgContent: svgstr });
            } catch (err) {
                self.postMessage({ error: err.message || "Unknown error" });
            }
        };
      `;
      return new Worker(URL.createObjectURL(new Blob([code], { type: 'application/javascript' })));
  };

  useEffect(() => {
    try {
        workerRef.current = createWorkerCallback();
        workerRef.current.onmessage = (event) => {
            const { svgContent, error } = event.data;
            if (error) {
                console.error("Worker error:", error);
                setLoading(false);
            } else {
                setSvgContent(svgContent);
                setLoading(false);
            }
        };
    } catch (e) {
        console.error("Worker Init Failed", e);
    }
    return () => {
        workerRef.current?.terminate();
    };
  }, []);

  useEffect(() => {
    // 1. Manage Object URL Lifecycle
    if (!file) return;
    const url = URL.createObjectURL(file);
    setOriginalImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    // 2. Trigger processing ONLY when the URL is ready and valid
    if (originalImageUrl && file) {
        processImage(originalImageUrl, options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalImageUrl]);

  const processImage = async (url: string, currentOptions: VectorizationOptions) => {
    if (!workerRef.current) return;
    setLoading(true);
    
    try {
        const img = new Image();
        // Set listeners BEFORE setting src
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = (e) => reject(new Error("Image load failed"));
            img.src = url;
        });

        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;
        setDimensions({ width: naturalWidth, height: naturalHeight });

        let width = naturalWidth;
        let height = naturalHeight;
        const MAX_DIM = 2500; 
        if (width > MAX_DIM || height > MAX_DIM) {
            const ratio = width / height;
            if (width > height) {
                width = MAX_DIM;
                height = Math.round(MAX_DIM / ratio);
            } else {
                height = MAX_DIM;
                width = Math.round(MAX_DIM * ratio);
            }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error("Could not get canvas context");
        
        ctx.drawImage(img, 0, 0, width, height);
        const imgData = ctx.getImageData(0, 0, width, height);

        const config = {
            ltres: currentOptions.simplify ? 10 : 0.1,
            qtres: currentOptions.simplify ? 10 : 0.1,
            pathomit: currentOptions.simplify ? 8 : 1,
            rightangleenhance: false,
            numberofcolors: currentOptions.colors,
            mincolorratio: 0,
            colorquantcycles: 3,
            scale: currentOptions.scale,
            strokewidth: currentOptions.strokewidth,
            linefilter: false,
        };

        workerRef.current.postMessage({ imgData, config });

    } catch (error) {
        console.error("Preparation failed:", error);
        setLoading(false);
    }
  };

  const handleUpdate = () => {
    if (originalImageUrl) {
      processImage(originalImageUrl, options);
    }
  };

  const handleDownload = () => {
    if (!svgContent) return;
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vector-${file.name.split('.')[0]}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header */}
      <header className="h-16 border-b px-6 flex items-center justify-between bg-white shadow-sm z-10">
        <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-positivus-dark">
                <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-bold text-xl flex items-center gap-3 text-positivus-dark">
                <div className="p-2 bg-positivus-green/20 rounded-lg">
                    <FileImageIcon className="w-5 h-5 text-positivus-dark" />
                </div>
                {copy.editor}
            </h1>
            <span className="text-sm font-medium text-gray-500 hidden md:inline-block bg-gray-100 px-3 py-1 rounded-full">
                {file.name}
            </span>
        </div>
        <div className="flex items-center gap-3">
            <button 
                onClick={handleDownload}
                disabled={!svgContent || loading}
                className="bg-positivus-dark text-white hover:bg-positivus-green hover:text-positivus-dark transition-all px-5 py-2.5 rounded-[15px] text-sm font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_0_0_#000000] active:translate-y-[2px] active:shadow-none"
            >
                <Download className="w-4 h-4" /> {copy.exportSvg}
            </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
         {/* Sidebar Controls */}
        <div className="w-80 border-r bg-white p-6 overflow-y-auto hidden md:block shadow-sm">
            <div className="space-y-8">
                {/* Mode Selector */}
                <div className="space-y-4">
                    <h3 className="text-base font-bold flex items-center gap-2 text-positivus-dark">
                        <Settings2 className="w-5 h-5" /> {copy.qualitySettings}
                    </h3>
                    <div className="p-4 bg-gray-50 rounded-[20px] border-2 border-positivus-dark/10 space-y-5">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-positivus-dark uppercase tracking-wide">{copy.fidelityMode}</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button 
                                    onClick={() => {
                                        const newOpts = { ...options, colors: 64, simplify: false };
                                        setOptions(newOpts);
                                        if (originalImageUrl) processImage(originalImageUrl, newOpts);
                                    }}
                                    className={`text-xs p-2.5 rounded-[10px] border-2 text-center transition-all font-bold ${!options.simplify && options.colors >= 32 ? 'bg-positivus-dark border-positivus-dark text-white' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-600'}`}
                                >
                                    {copy.photoHigh}
                                </button>
                                <button 
                                    onClick={() => {
                                        const newOpts = { ...options, colors: 16, simplify: true };
                                        setOptions(newOpts);
                                        if (originalImageUrl) processImage(originalImageUrl, newOpts);
                                    }}
                                    className={`text-xs p-2.5 rounded-[10px] border-2 text-center transition-all font-bold ${options.simplify ? 'bg-positivus-dark border-positivus-dark text-white' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-600'}`}
                                >
                                    {copy.logoClean}
                                </button>
                            </div>
                        </div>

                         <div className="space-y-2">
                            <div className="flex justify-between">
                                <label className="text-xs font-bold text-positivus-dark uppercase tracking-wide">{copy.colors}</label>
                                <span className="text-xs font-bold bg-positivus-green/20 px-2 py-0.5 rounded text-positivus-dark">{options.colors}</span>
                            </div>
                           <input 
                                type="range"
                                min="2"
                                max="256"
                                step="1"
                                value={options.colors}
                                onChange={(e) => setOptions({...options, colors: Number(e.target.value)})}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-positivus-dark"
                            />
                        </div>

                        <div className="flex items-center justify-between pt-2">
                             <label className="text-xs font-bold text-positivus-dark uppercase tracking-wide cursor-pointer" htmlFor="simplify-check">{copy.simplifyPaths}</label>
                             <input 
                                type="checkbox"
                                id="simplify-check"
                                checked={options.simplify}
                                onChange={(e) => setOptions({...options, simplify: e.target.checked})}
                                className="h-5 w-5 rounded border-2 border-positivus-dark/20 text-positivus-dark focus:ring-positivus-dark"
                             />
                        </div>
                    </div>
                </div>

                <div className="pt-2">
                    <button 
                        onClick={handleUpdate}
                        disabled={loading}
                        className="w-full py-3 bg-white border-2 border-positivus-dark text-positivus-dark hover:bg-gray-50 rounded-[15px] text-sm font-bold flex items-center justify-center gap-2 transition-all"
                    >
                       {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                       {copy.reprocessImage}
                    </button>
                </div>
            </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 bg-muted/30 relative flex flex-col min-w-0">
             {/* Toolbar */}
             <div className="h-16 border-b bg-white flex items-center justify-between gap-4 px-6 shadow-sm z-10">
                <div className="flex items-center gap-2">
                    <button onClick={() => setZoom(z => Math.max(0.1, z - 0.1))} className="p-2 hover:bg-gray-100 rounded-[10px] transition-colors text-positivus-dark bg-white border border-gray-200" title={copy.zoomOut}>
                        <ZoomOut className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-bold w-12 text-center text-positivus-dark bg-gray-100 py-2 rounded-[8px]">{Math.round(zoom * 100)}%</span>
                     <button onClick={() => setZoom(z => Math.min(5, z + 0.1))} className="p-2 hover:bg-gray-100 rounded-[10px] transition-colors text-positivus-dark bg-white border border-gray-200" title={copy.zoomIn}>
                        <ZoomIn className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex items-center gap-2">
                     <button 
                        onMouseDown={() => setViewMode('original')}
                        onMouseUp={() => setViewMode('vector')}
                        onMouseLeave={() => setViewMode('vector')}
                        className="px-4 py-2 text-xs font-bold rounded-[10px] bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors"
                    >
                        {copy.holdToSeeOriginal}
                    </button>
                </div>
             </div>

             {/* Loading Overlay - Centered in Main Area */}
             {loading && (
                <div className="absolute inset-0 z-50 bg-white/80 flex items-center justify-center backdrop-blur-[1px]">
                     <div className="flex flex-col items-center gap-3 bg-white p-6 rounded-[20px] shadow-2xl border border-gray-100 animate-in fade-in zoom-in">
                        <RefreshCw className="w-8 h-8 animate-spin text-positivus-dark" />
                        <span className="text-sm font-bold text-positivus-dark">{copy.vectorizing}</span>
                     </div>
                </div>
            )}

             {/* Rendering Stage */}
             <div className="flex-1 overflow-auto p-8 bg-gray-50 flex items-center justify-center">
                 <div 
                    className="relative shadow-2xl bg-white"
                    style={{ 
                        transform: `scale(${zoom})`, 
                        transformOrigin: 'center center',
                        aspectRatio: dimensions ? `${dimensions.width} / ${dimensions.height}` : 'auto',
                        maxWidth: '80vw',
                        maxHeight: '70vh',
                        width: '100%',
                        height: 'auto',
                        backgroundImage: `
                            linear-gradient(45deg, #f0f0f0 25%, transparent 25%), 
                            linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), 
                            linear-gradient(45deg, transparent 75%, #f0f0f0 75%), 
                            linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
                        `,
                        backgroundSize: '20px 20px',
                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                    }}
                 >
                    {viewMode === 'original' && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img 
                            src={originalImageUrl} 
                            alt={copy.original} 
                            className="w-full h-full object-contain block" 
                        />
                    )}

                    {viewMode === 'vector' && svgContent && (
                        <div 
                            dangerouslySetInnerHTML={{ __html: svgContent }} 
                            className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:block" 
                        />
                    )}
                    
                     {viewMode === 'vector' && !svgContent && (
                        <div className="w-full h-full min-h-[300px] flex items-center justify-center text-muted-foreground animate-pulse bg-gray-100 rounded-lg">
                            <span className="text-sm font-medium text-gray-400">{copy.waiting}</span>
                        </div>
                    )}
                 </div>
             </div>
        </div>
      </div>
    </div>
  );
}
